'use client';
import React from 'react';

interface InputRowProps {
  label: string;
  unit: string;
  note?: string;
  confidence?: 'High' | 'Medium' | 'Low';
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  isPercent?: boolean;
  isCalculated?: boolean;
  calculatedValue?: string;
  formatDisplay?: (v: number) => string;
}

export default function InputRow({
  label,
  unit,
  note,
  confidence,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  isPercent = false,
  isCalculated = false,
  calculatedValue,
  formatDisplay,
}: InputRowProps) {
  const displayValue = isPercent ? value * 100 : value;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = parseFloat(e.target.value);
    if (isNaN(raw)) return;
    const final = isPercent ? raw / 100 : raw;
    onChange(final);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 items-start sm:items-center py-3 border-b border-border/50 last:border-0">
      {/* Label + note */}
      <div>
        <label className="text-sm font-medium text-foreground block">{label}</label>
        {note && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{note}</p>}
      </div>

      {/* Unit */}
      <div className="hidden sm:block text-xs text-muted-foreground font-mono whitespace-nowrap">
        {unit}
      </div>

      {/* Input or calculated */}
      <div className="w-full sm:w-36">
        {isCalculated ? (
          <div className="input-calculated">{calculatedValue ?? '—'}</div>
        ) : (
          <input
            type="number"
            className="input-editable"
            value={displayValue}
            onChange={handleChange}
            min={min}
            max={max}
            step={step}
            aria-label={label}
          />
        )}
      </div>

      {/* Confidence badge */}
      <div className="hidden sm:flex items-center">
        {confidence === 'High' && <span className="confidence-high">High</span>}
        {confidence === 'Medium' && <span className="confidence-medium">Medium</span>}
        {confidence === 'Low' && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-danger border border-red-200">
            Low
          </span>
        )}
      </div>
    </div>
  );
}
